/**
 * Tests for the CanvasLayoutFindings display surface (audit R25; spec 13.4).
 *
 * The surface must: render nothing when clean; show blocking errors distinctly
 * from reported normalizations; and, for a LAY004 warn, name the from -> to
 * growth so the normalization is visibly never silent.
 */

import "@testing-library/jest-dom";
import React from "react";
import { render, screen } from "@testing-library/react";
import { CssVarsProvider } from "@mui/joy/styles";
import { CanvasLayoutFindings } from "./CanvasLayoutFindings";
import type { LayFinding } from "../../utils/canvasLayoutFindings";

function renderSurface(findings: LayFinding[]) {
  return render(
    <CssVarsProvider>
      <CanvasLayoutFindings findings={findings} data-testid="surface" />
    </CssVarsProvider>,
  );
}

describe("CanvasLayoutFindings", () => {
  it("renders nothing when there are no findings", () => {
    const { container } = renderSurface([]);
    expect(container).toBeEmptyDOMElement();
    expect(screen.queryByTestId("surface")).toBeNull();
  });

  it("renders a blocking-error alert for an error finding", () => {
    const findings: LayFinding[] = [
      {
        code: "LAY001",
        severity: "error",
        elementId: "a",
        message: "Element extends past the 12-column grid.",
      },
    ];
    renderSurface(findings);
    expect(screen.getByTestId("surface-errors")).toBeInTheDocument();
    expect(screen.queryByTestId("surface-warnings")).toBeNull();
    expect(screen.getByText("1 layout problem blocks saving")).toBeInTheDocument();
    expect(screen.getByText("LAY001")).toBeInTheDocument();
    expect(
      screen.getByText("Element extends past the 12-column grid."),
    ).toBeInTheDocument();
  });

  it("renders a warning alert that names the from -> to normalization for LAY004", () => {
    const findings: LayFinding[] = [
      {
        code: "LAY004",
        severity: "warn",
        elementId: "cnt",
        message: "Element is below the minimum height for its type; grown to 4 rows and reported (not silently applied).",
        normalization: { field: "rowSpan", from: 1, to: 4 },
      },
    ];
    renderSurface(findings);
    expect(screen.getByTestId("surface-warnings")).toBeInTheDocument();
    expect(screen.queryByTestId("surface-errors")).toBeNull();
    expect(
      screen.getByText("1 element was normalized (saved, not blocked)"),
    ).toBeInTheDocument();
    // The from -> to growth must be visible so it is never silent.
    expect(
      screen.getByText(/height: 1 -> 4 \(applied and reported\)/),
    ).toBeInTheDocument();
  });

  it("renders errors and warnings together, each in its own alert", () => {
    const findings: LayFinding[] = [
      {
        code: "LAY003",
        severity: "error",
        elementId: "one",
        message: "Elements overlap.",
      },
      {
        code: "LAY004",
        severity: "warn",
        elementId: "cnt",
        message: "Grown to minimum width.",
        normalization: { field: "columnSpan", from: 1, to: 2 },
      },
    ];
    renderSurface(findings);
    expect(screen.getByTestId("surface-errors")).toBeInTheDocument();
    expect(screen.getByTestId("surface-warnings")).toBeInTheDocument();
    expect(screen.getByText(/width: 1 -> 2 \(applied and reported\)/)).toBeInTheDocument();
  });

  it("pluralizes error and warning counts correctly", () => {
    const findings: LayFinding[] = [
      { code: "LAY001", severity: "error", elementId: "a", message: "m1" },
      { code: "LAY002", severity: "error", elementId: "b", message: "m2" },
    ];
    renderSurface(findings);
    expect(screen.getByText("2 layout problems block saving")).toBeInTheDocument();
  });
});
